/**
 * Mask a string with a given character from a given index to another given index
 * @param content The string to mask
 * @param options The options to mask the string
 * @param options.maskWith The character to use to mask the string
 * @param options.from The index to start masking the string
 * @param options.to The index to stop masking the string
 * @returns The masked string
 *
 * @example
 * ```ts
 * maskContent('hello world'); // "***********"
 * maskContent('hello world', { from: 0, to: 5 }); // "***** world"
 * maskContent('hello world', { to: 5 }); // "hello *****"
 * maskContent('hello world', { from: 2, to: 7 }); // "he*****orld"
 * ```
 */
function maskContent(
  content: string,
  options: { maskWith?: string; from?: number; to?: number } = {}
) {
  if (!content) return content;

  const { maskWith = '*', from = 0, to = content.length } = options;

  const contentLength = content.length;
  const maskLength = to - from;

  const mask = maskWith.repeat(maskLength);
  const maskedString = content.slice(0, from) + mask + content.slice(to, contentLength);

  return maskedString;
}

/**
 * Test a string if it is alphanumeric
 * @param content The string to test
 * @returns A boolean indicating if the string is alphanumeric
 * @example
 * ```ts
 * isAlphanumeric('hello world'); // false
 * isAlphanumeric('helloWorld'); // true
 * isAlphanumeric('helloWorld123'); // true
 * isAlphanumeric('helloWorld123!'); // false
 * ```
 */
function isAlphanumeric(content: string) {
  return /^[a-z0-9]+$/i.test(content);
}

const generateActionName = <X extends string, Y extends string>(
  reducer: X,
  action: Y
): `${Uppercase<X>}/${Y}` => `${reducer.toUpperCase() as Uppercase<X>}/${action}`;

export { maskContent, isAlphanumeric, generateActionName };
